// A time pattern is a named list of multipliers whose real LENGTH depends
// on its own Type. Real files wrap that list across several rows, Type
// only on the pattern's own first row.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";

export interface Pattern {
  name: string;
  type: string;
  multipliers: number[];
}

/** WEEKEND is its own type, not a variant of DAILY — a 24-hour pattern applied only on Saturdays/Sundays instead of DAILY's own per-weekday multiplier. */
export const PATTERN_TYPES = ["MONTHLY", "DAILY", "HOURLY", "WEEKEND"] as const;

const PATTERN_TYPE_SET = new Set<string>(PATTERN_TYPES);

const PATTERN_MULTIPLIER_COUNT: Record<string, number> = {
  MONTHLY: 12,
  DAILY: 7,
  HOURLY: 24,
  WEEKEND: 24,
};

// Not a hard SWMM requirement — just the convention this app's own writer
// follows too, so a re-exported file reads the same way a human-edited one
// would.
const VALUES_PER_ROW = 6;

export function readPatterns(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Pattern[] {
  const patterns: Pattern[] = [];
  const byName = new Map<string, Pattern>();
  for (const row of raw.get("PATTERNS") ?? []) {
    const name = row[0];
    if (!name) continue;
    let pattern = byName.get(name);
    if (!pattern) {
      const hasType = PATTERN_TYPE_SET.has((row[1] ?? "").toUpperCase());
      pattern = { name, type: hasType ? row[1].toUpperCase() : PATTERN_TYPES[2], multipliers: [] };
      byName.set(name, pattern);
      patterns.push(pattern);
      for (let i = hasType ? 2 : 1; i < row.length; i++) {
        pattern.multipliers.push(parseNum(row[i], 1, `[PATTERNS] "${name}" multiplier`, warnings));
      }
    } else {
      for (let i = 1; i < row.length; i++) {
        pattern.multipliers.push(parseNum(row[i], 1, `[PATTERNS] "${name}" multiplier`, warnings));
      }
    }
  }
  return patterns;
}

export function writePatterns(
  raw: ReadonlyMap<string, string[][]>,
  patterns: Pattern[]
): Map<string, string[][]> {
  const rows: string[][] = [];
  for (const pattern of patterns) {
    if (pattern.multipliers.length === 0) {
      rows.push([pattern.name, pattern.type]);
      continue;
    }
    for (let i = 0; i < pattern.multipliers.length; i += VALUES_PER_ROW) {
      const chunk = pattern.multipliers.slice(i, i + VALUES_PER_ROW).map(String);
      rows.push(i === 0 ? [pattern.name, pattern.type, ...chunk] : [pattern.name, ...chunk]);
    }
  }
  const next = new Map(raw);
  next.set("PATTERNS", rows);
  return next;
}

/** Pads with 1 (SWMM's own neutral multiplier — no adjustment from the time-average, unlike 0 which would mean "no flow at all"), not 0. */
export function resizeMultipliers(multipliers: number[], type: string): number[] {
  const count = PATTERN_MULTIPLIER_COUNT[type] ?? multipliers.length;
  if (multipliers.length === count) return multipliers;
  if (multipliers.length > count) return multipliers.slice(0, count);
  return [...multipliers, ...Array(count - multipliers.length).fill(1)];
}

export function nextAvailablePatternName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "P");
}
